import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Button,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import { 
  Add, 
  Event, 
  Group, 
  Description, 
  CheckCircle,
  TrendingUp
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { getPartnerProfile, addActivity } from '../api';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

const Activities = () => {
  const { partnerId } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activityForm, setActivityForm] = useState({
    name: '',
    description: '',
    num_people: '',
  });
  const [partnerData, setPartnerData] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      setError(null);
      try {
        const profile = await getPartnerProfile(partnerId);
        setPartnerData(profile);
        setActivities(profile.activities || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [partnerId]);

  const handleActivitySubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccessMessage('');
      
      if (!activityForm.name || !activityForm.num_people) {
        throw new Error('Name and number of people are required');
      }
      const numPeople = parseInt(activityForm.num_people);
      if (isNaN(numPeople) || numPeople < 0) {
        throw new Error('Number of people must be a non-negative integer');
      }

      await addActivity({
        partner_id: partnerId,
        name: activityForm.name,
        description: activityForm.description,
        num_people: numPeople,
      });

      const updatedProfile = await getPartnerProfile(partnerId);
      setActivities(updatedProfile.activities || []);
      setActivityForm({ name: '', description: '', num_people: '' });
      setError(null);
      setSuccessMessage('Activity added successfully!');
      setTimeout(() => setSuccessMessage(''), 5000); // Clear message after 5 seconds
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate statistics
  const totalActivities = activities.length;
  const totalParticipants = activities.reduce((sum, activity) => sum + (activity.num_people || 0), 0);
  const averageParticipants = totalActivities > 0 ? Math.round(totalParticipants / totalActivities) : 0;

  return (
    <Box sx={{ 
      p: isMobile ? 2 : 4, 
      bgcolor: '#f8f9fa', 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)'
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        
        {partnerData && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexDirection: isMobile ? 'column' : 'row',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            
            {partnerData.name && (
              <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                {partnerData.name}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {loading && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          my: 8 
        }}>
          <CircularProgress size={60} sx={{ color: '#ff69b4' }} />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 4, borderRadius: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 4, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Statistics Cards */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'row', 
            gap: 2, 
            mb: 4, 
            width: '100%',
            flexWrap: 'wrap'
          }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
              background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    {totalActivities}
                  </Typography>
                  <Event sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Total Activities
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Organized by partner
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(187, 255, 193, 0.12)',
              background: 'linear-gradient(135deg, #BBFFC1 0%, #a8ffae 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    {totalParticipants}
                  </Typography>
                  <Group sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Total Participants
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Across all activities
                </Typography>
              </CardContent>
            </Card>

            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 8px 32px rgba(249, 41, 85, 0.12)',
              background: 'linear-gradient(135deg, #f92955 0%, #d41e3f 100%)',
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              flex: '1 1 250px',
              minWidth: '250px',
              maxWidth: '100%',
            }}>
              <Box sx={{
                position: 'absolute',
                top: -20,
                right: -20,
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.1)',
              }} />
              <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, fontSize: '2.5rem' }}>
                    {averageParticipants}
                  </Typography>
                  <TrendingUp sx={{ fontSize: 40, opacity: 0.8 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  Avg Participants
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Per activity
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Add Activity Form */}
          <Paper sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            background: '#fff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                mr: 2, 
                color: '#ff69b4',
                p: 1,
                borderRadius: 2,
                bgcolor: '#ff69b415',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Add sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
                Add New Activity
              </Typography>
            </Box>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Activity Name"
                  value={activityForm.name}
                  onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#ff69b4',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff69b4',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Description"
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#ff69b4',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff69b4',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Number of People"
                  type="number"
                  value={activityForm.num_people}
                  onChange={(e) => setActivityForm({ ...activityForm, num_people: e.target.value })}
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '&:hover fieldset': {
                        borderColor: '#ff69b4',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#ff69b4',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  variant="contained"
                  onClick={handleActivitySubmit}
                  disabled={submitting}
                  fullWidth
                  sx={{ 
                    bgcolor: '#ff69b4',
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                    '&:hover': {
                      bgcolor: '#e91e63',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(255, 105, 180, 0.3)',
                    },
                    '&:disabled': {
                      bgcolor: '#ccc',
                      transform: 'none',
                      boxShadow: 'none',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {submitting ? (
                    <CircularProgress size={18} sx={{ color: '#fff', mr: 1 }} />
                  ) : (
                    <Add sx={{ mr: 1, fontSize: 18 }} />
                  )}
                  {submitting ? 'Adding...' : 'Add Activity'}
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Activities List */}
          <Paper sx={{ 
            p: 3, 
            borderRadius: 3, 
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            border: '1px solid #f0f0f0',
            background: '#fff'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box sx={{ 
                mr: 2, 
                color: '#4caf50',
                p: 1,
                borderRadius: 2,
                bgcolor: '#4caf5015',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Event sx={{ fontSize: 20 }} />
              </Box>
              <Typography variant="h6" sx={{ color: '#333', fontWeight: 600 }}>
                Activities List
              </Typography>
              <Chip 
                label={`${activities.length} activities`}
                sx={{ 
                  ml: 'auto',
                  bgcolor: '#ff69b415',
                  color: '#ff69b4',
                  fontWeight: 600,
                  fontSize: '0.8rem'
                }}
              />
            </Box>

            {activities.length === 0 ? (
              <Box sx={{ 
                textAlign: 'center', 
                py: 6,
                color: '#666'
              }}>
                <Event sx={{ fontSize: 48, color: '#ddd', mb: 2 }} />
                <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>
                  No Activities Found
                </Typography>
                <Typography variant="body2" sx={{ color: '#999' }}>
                  Start by adding your first activity above
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {activities.map((activity, index) => (
                  <Grid item xs={12} md={6} lg={4} key={activity.activity_id}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: 3,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                        border: '1px solid #f0f0f0',
                        background: '#fff',
                        transition: 'all 0.3s ease',
                        '&:hover': { 
                          transform: 'translateY(-4px)',
                          boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
                        },
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '3px',
                          background: `linear-gradient(90deg, ${index % 3 === 0 ? '#ff69b4' : index % 3 === 1 ? '#4caf50' : '#2196f3'}, ${index % 3 === 0 ? '#ff69b488' : index % 3 === 1 ? '#4caf5088' : '#2196f388'})`,
                        }
                      }}
                    >
                      <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ 
                            mr: 2, 
                            color: index % 3 === 0 ? '#ff69b4' : index % 3 === 1 ? '#4caf50' : '#2196f3',
                            p: 1,
                            borderRadius: 2,
                            bgcolor: index % 3 === 0 ? '#ff69b415' : index % 3 === 1 ? '#4caf5015' : '#2196f315',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <Event sx={{ fontSize: 20 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ 
                              color: '#333', 
                              fontWeight: 600,
                              fontSize: '1rem',
                              mb: 0.5
                            }}>
                              {activity.name}
                            </Typography>
                            <Chip 
                              label={`ID: ${activity.activity_id}`}
                              size="small"
                              sx={{ 
                                bgcolor: '#f0f0f0',
                                color: '#666',
                                fontSize: '0.7rem',
                                height: 20
                              }}
                            />
                          </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" sx={{ 
                            color: '#666', 
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center'
                          }}>
                            <Description sx={{ fontSize: 16, mr: 1, color: '#999' }} />
                            Description
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            color: '#333',
                            fontSize: '0.9rem',
                            lineHeight: 1.4
                          }}>
                            {activity.description || 'No description provided'}
                          </Typography>
                        </Box>

                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          bgcolor: '#f8f9fa',
                          p: 1.5,
                          borderRadius: 2
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Group sx={{ fontSize: 18, color: '#4caf50', mr: 1 }} />
                            <Typography variant="body2" sx={{ color: '#666', fontWeight: 600 }}>
                              Participants
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" sx={{ 
                              color: '#4caf50', 
                              fontWeight: 700,
                              fontSize: '1.2rem'
                            }}>
                              {activity.num_people || 0}
                            </Typography>
                            <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default Activities;